import { useCallback, useEffect, useMemo, useState } from 'react'

import { type Timestamp } from '@bufbuild/protobuf/wkt'
import {
  SplitSupplyNoticeSection,
  SplitSupplyPartnerNoticeFillState,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import {
  Flex,
  OutlineDangerButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaTrashCanRegular } from '@enechain/ecloud-icons'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import { AuditCell } from '~/components/common/MasterDataTable'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { iconSize } from '~/constants/iconSize'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { convertAreaLabel } from '~/models/area'
import { type CivilDate, protoDateToCivilDate } from '~/models/civilDate'
import {
  PARTNER_NOTICE_CHANNEL_KEYS,
  isPartnerNoticeEdited,
  isStartEditingState,
  parsePartnerNoticeDetailSearch,
  partnerNoticeDetailSearch,
} from '~/pages/SplitSupplyPlan/models/partnerNotice'
import { formatCivilDay } from '~/pages/SplitSupplyPlan/models/period'
import {
  describeSplitSupplyNoticeDeleteError,
  describeSplitSupplyNoticeError,
} from '~/pages/SplitSupplyPlan/models/serverError'
import { InfoItem } from '~/pages/SplitSupplyPlanDetail/components/InfoItem'
import { NoticeDetailStatus } from '~/pages/SplitSupplyPlanDetail/components/NoticeDetailStatus'
import { NoticeDetailToolbar } from '~/pages/SplitSupplyPlanDetail/components/NoticeDetailToolbar'
import { NoticeValueGrid } from '~/pages/SplitSupplyPlanDetail/components/NoticeValueGrid'
import { useDeletePartnerSplitSupplyNotice } from '~/pages/SplitSupplyPlanDetail/hooks/useDeletePartnerSplitSupplyNotice'
import { useGetPartnerSplitSupplyNotice } from '~/pages/SplitSupplyPlanDetail/hooks/useGetPartnerSplitSupplyNotice'
import { useNoticeDetailView } from '~/pages/SplitSupplyPlanDetail/hooks/useNoticeDetailView'
import { useNoticeDraftEditing } from '~/pages/SplitSupplyPlanDetail/hooks/useNoticeDraftEditing'
import { useSavePartnerSplitSupplyNoticeValues } from '~/pages/SplitSupplyPlanDetail/hooks/useSavePartnerSplitSupplyNoticeValues'
import {
  dayCountOf,
  toPartnerSaveRequestPoints,
} from '~/pages/SplitSupplyPlanDetail/models/draft'
import { routePaths } from '~/routes/routePaths'

const NS = 'page:splitSupplyPlanDetail' as const

// 入力の状態 (期間の全地点 × 全日 × 48 コマで判定)。 残りの入力がある間は警告色
const FILL_STATE_BADGE: Record<
  SplitSupplyPartnerNoticeFillState,
  EnumBadgeSpec
> = {
  [SplitSupplyPartnerNoticeFillState.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: `${NS}.partner.fillState.unknown`,
  },
  [SplitSupplyPartnerNoticeFillState.EMPTY]: {
    status: 'warning',
    labelKey: `${NS}.partner.fillState.empty`,
  },
  [SplitSupplyPartnerNoticeFillState.PARTIAL]: {
    status: 'warning',
    labelKey: `${NS}.partner.fillState.partial`,
  },
  [SplitSupplyPartnerNoticeFillState.COMPLETE]: {
    status: 'positive',
    labelKey: `${NS}.partner.fillState.complete`,
  },
}

/**
 * 他社の通告値 (登録 1 件) の詳細。 登録の全地点 × 48 コマを見て、 手で直して保存する。
 * 他社が出した通告値の写しなので、 提出も XML のダウンロードも無い。 手直しはこの登録の値を置き換え、
 * 登録日時・登録者・登録方法は変えずに最終更新だけが付く。 受渡日を過ぎた登録も直せる。
 * 月間の登録は 「1 日 / 月全体」 を切り替えられ、 表 (`NoticeValueGrid`) と操作の行 (`NoticeDetailToolbar`)・
 * 取得状態 (`NoticeDetailStatus`) は自社の通告値の詳細と共通。 保存は表示している全日 × 全地点を送る。
 */
export const PartnerNoticeDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useMemo(
    () => parsePartnerNoticeDetailSearch(searchParams),
    [searchParams],
  )
  const id = location?.id
  const deliveryDate = location?.deliveryDate

  // 登録の断面は取得して初めて分かる (URL は登録の id と日だけを持つ)
  const [loadedSection, setLoadedSection] = useState<
    SplitSupplyNoticeSection | undefined
  >(undefined)
  const isMonthly = loadedSection === SplitSupplyNoticeSection.MONTHLY
  const { view, setView, wholeMonth } = useNoticeDetailView(isMonthly)

  const { data, isLoading, isError, error, refetch } =
    useGetPartnerSplitSupplyNotice(id, wholeMonth ? 'all' : deliveryDate)
  if (data !== undefined && data.section !== loadedSection) {
    setLoadedSection(data.section)
  }
  const { mutate: save, isPending: isSaving } =
    useSavePartnerSplitSupplyNoticeValues()
  const { mutate: deleteNotice, isPending: isDeleting } =
    useDeletePartnerSplitSupplyNotice()
  // 確認ダイアログ: 閲覧中の 「削除」 ('delete') と、 一度も保存していない空の登録の編集中の 「破棄」 ('discard')。
  // どちらも登録ごと消す同じ処理で、 本文だけが違う
  const [deleteDialog, setDeleteDialog] = useState<
    'delete' | 'discard' | undefined
  >(undefined)
  // 削除の楽観ロックの基準は、 確認を開いた時点で固める (閲覧中は開いた時の最終更新、 破棄は編集を始めた時の最終更新)。
  // 確認を開いている間に詳細を取り直しても変えず、 その間に他の人が保存していたら競合として消さない
  const [deleteBaseline, setDeleteBaseline] = useState<Timestamp | undefined>(
    undefined,
  )
  // 消えた後の一覧への移動は、 編集の状態を解いた後に行う (編集中のままだと未保存の確認がもう一度出る)
  const [leaveAfterDelete, setLeaveAfterDelete] = useState(false)

  // 「追加」 で新しく作った直後は、 「編集」 を押した後と同じ状態で開く。 route state は受け取ったらすぐ消し、
  // 再読込や戻るで勝手に編集モードにならないようにする (一覧の 「詳細」 から開いた時は state が無く閲覧)
  const startInEditing = isStartEditingState(useLocation().state)
  const clearStartState = useCallback(() => {
    setSearchParams(searchParams, { replace: true, state: null })
  }, [searchParams, setSearchParams])

  const fetchedPoints = useMemo(() => data?.points ?? [], [data])
  const {
    points,
    isEditing,
    draft,
    baseline,
    invalidCells,
    guard,
    startEditing,
    finishEditing,
    setEdited,
  } = useNoticeDraftEditing({
    fetchedPoints,
    updatedAt: data?.updatedAt,
    wholeMonth,
    deliveryDate,
    startEditingWhen: startInEditing && data !== undefined,
    onStartedEditing: clearStartState,
  })

  useEffect(() => {
    if (!leaveAfterDelete || isEditing) return
    void navigate(`${routePaths.planSplitSupply.path}?side=partner`)
  }, [leaveAfterDelete, isEditing, navigate])

  const openDeleteDialog = (
    kind: 'delete' | 'discard',
    expectedUpdatedAt: Timestamp | undefined,
  ): void => {
    setDeleteBaseline(expectedUpdatedAt)
    setDeleteDialog(kind)
  }

  const goToDay = (day: CivilDate): void => {
    if (id === undefined) return
    setSearchParams(partnerNoticeDetailSearch(id, day), { replace: true })
  }

  // 保存は表示している全日 × 全地点を送る (1 日の表示ならその日、 月全体なら対象月の全日)。
  // 楽観ロックの基準は編集を始めた時点の最終更新 (編集中に取得し直されても変えない)
  const handleSave = (): void => {
    if (id === undefined || baseline === undefined) return
    save(
      {
        id,
        points: toPartnerSaveRequestPoints(
          points,
          draft,
          Array.from({ length: dayCountOf(points) }, (_, i) => i),
        ),
        expectedUpdatedAt: baseline,
      },
      {
        onSuccess: () => {
          finishEditing()
          showSuccessToast({ title: t(`${NS}.save.successTitle`) })
        },
        onError: (err) => {
          showErrorToast({
            title: t(`${NS}.save.errorTitle`),
            description: describeSplitSupplyNoticeError(err, t),
          })
        },
      },
    )
  }

  // 削除は確認を開いた時に固めた最終更新を楽観ロックの基準にし、 消えたら一覧 (他社の通告値のタブ) へ戻る。
  // 他の人が先に直した・消した場合や、 消せない期間の登録は消さず、 理由を知らせて閉じる
  const handleDelete = (): void => {
    if (id === undefined || deleteBaseline === undefined) return
    deleteNotice(
      { id, expectedUpdatedAt: deleteBaseline },
      {
        onSuccess: () => {
          setDeleteDialog(undefined)
          showSuccessToast({ title: t(`${NS}.partner.delete.successTitle`) })
          finishEditing()
          setLeaveAfterDelete(true)
        },
        onError: (err) => {
          setDeleteDialog(undefined)
          showErrorToast({
            title: t(`${NS}.partner.delete.errorTitle`),
            description: describeSplitSupplyNoticeDeleteError(err, t),
          })
        },
      },
    )
  }

  const breadcrumbItems = [
    { label: t('common:nav.plan') },
    {
      label: t('common:route.planSplitSupply'),
      to: `${routePaths.planSplitSupply.path}?side=partner`,
    },
    { label: t('common:route.planSplitSupplyPartnerDetail'), bold: true },
  ]

  const target = protoDateToCivilDate(data?.targetDate)
  const channelKey =
    data === undefined ? undefined : PARTNER_NOTICE_CHANNEL_KEYS[data.channel]

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        <NoticeDetailStatus
          errorCode={error?.code}
          hasData={data !== undefined}
          hasLocation={location !== undefined}
          isError={isError}
          isLoading={isLoading}
          messages={{
            invalidLink: t(`${NS}.partner.invalidLink`),
            notFound: t(`${NS}.partner.notFound`),
            fetchFailed: t(`${NS}.partner.fetchFailed`),
          }}
          onRetry={() => {
            void refetch()
          }}
        >
          {data !== undefined && (
            <>
              <Flex alignItems="flex-start" flexWrap="wrap" gap={6}>
                <InfoItem label={t(`${NS}.partner.info.status`)}>
                  <EnumStatusBadge
                    map={FILL_STATE_BADGE}
                    value={data.fillState}
                  />
                </InfoItem>
                <InfoItem label={t(`${NS}.info.period`)}>
                  {isMonthly
                    ? t('page:splitSupplyPlan.monthLabel', {
                        year: target?.year,
                        month: target?.month,
                      })
                    : formatCivilDay(target)}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.area`)}>
                  {convertAreaLabel(data.areaCode)}
                </InfoItem>
                <InfoItem label={t(`${NS}.partner.info.counterparty`)}>
                  {`${data.counterpartyBusinessCode} ${data.counterpartyBusinessName}`}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.pointCount`)}>
                  {t(`${NS}.partner.info.pointCountValue`, {
                    count: data.points.length,
                  })}
                </InfoItem>
                <InfoItem label={t(`${NS}.partner.info.registered`)}>
                  <AuditCell
                    updatedAt={data.createdAt}
                    updatedBy={data.createdBy}
                  />
                </InfoItem>
                <InfoItem label={t(`${NS}.partner.info.channel`)}>
                  {channelKey === undefined ? '—' : t(channelKey)}
                  {/* 取込で登録した時は、 その通に使ったファイルの名前 (改行でつないで記録したもの。名前に「、」が入ってもそのまま 1 つの名前) を 1 ファイル 1 行で出す */}
                  {data.sourceFileName
                    .split('\n')
                    .filter((name) => name !== '')
                    .map((name, index) => (
                      <Text
                        // 同じ名前のファイルが並びうるので、 並びの位置も key に入れる
                        key={`${index}|${name}`}
                        textStyle="caption"
                        display="block"
                        fontWeight="normal"
                        whiteSpace="nowrap"
                      >
                        {name}
                      </Text>
                    ))}
                </InfoItem>
                <InfoItem label={t(`${NS}.info.updated`)}>
                  {isPartnerNoticeEdited(data.createdAt, data.updatedAt) ? (
                    <AuditCell
                      updatedAt={data.updatedAt}
                      updatedBy={data.updatedBy}
                    />
                  ) : (
                    '—'
                  )}
                </InfoItem>
              </Flex>

              {/* 注記の行に並べる (行を増やさない)。 過ぎた時間 (受渡日が今日以前・月間は今月以前) の登録を
                  編集している間だけ、 取り込み済みの実績に反映されない注意を足す */}
              <Flex alignItems="baseline" flexWrap="wrap" columnGap={4}>
                <Text textStyle="caption" color="Text.midEmphasis">
                  {t(`${NS}.partner.note`)}
                </Text>
                {isEditing && data.past && (
                  <Text textStyle="caption" color="Text.warningMid">
                    {t('page:splitSupplyPlan.partner.pastWarning')}
                  </Text>
                )}
              </Flex>

              <NoticeDetailToolbar
                deliveryDate={deliveryDate}
                guardAction={guard.guardAction}
                invalidCount={invalidCells.length}
                isEditing={isEditing}
                isMonthly={isMonthly}
                isSaving={isSaving}
                onDayChange={goToDay}
                onDiscard={() => {
                  // 一度も保存していない空の登録の破棄は、 登録ごと消す (確認を挟む)。 保存したことのある登録は入力を捨てるだけ
                  if (data.empty) openDeleteDialog('discard', baseline)
                  else finishEditing()
                }}
                onSave={handleSave}
                onSetView={setView}
                target={target}
                view={view}
                viewActions={
                  <>
                    {/* 消せるのは翌日以降の登録か、 一度も保存していない空の登録 (過ぎた登録でも手直しはできる) */}
                    {data.deletable && (
                      <OutlineDangerButton
                        onClick={() => {
                          openDeleteDialog('delete', data.updatedAt)
                        }}
                        size="md"
                      >
                        <FaTrashCanRegular boxSize={iconSize.labeledButton} />
                        {t(`${NS}.partner.delete.button`)}
                      </OutlineDangerButton>
                    )}
                    <TertiaryButton onClick={startEditing} size="md">
                      {t(`${NS}.edit`)}
                    </TertiaryButton>
                  </>
                }
                wholeMonth={wholeMonth}
              />

              <Text textStyle="caption" color="Text.midEmphasis">
                {t(`${NS}.unit`)}
              </Text>
              <NoticeValueGrid
                draft={draft}
                isEditing={isEditing && !isSaving}
                onChange={setEdited}
                points={points}
              />
            </>
          )}
        </NoticeDetailStatus>
      </PageBody>
      <ConfirmDialog
        body={t(
          deleteDialog === 'discard'
            ? `${NS}.partner.delete.discardBody`
            : `${NS}.partner.delete.body`,
        )}
        confirmLabel={t('common:action.delete')}
        destructive
        isLoading={isDeleting}
        isOpen={deleteDialog !== undefined}
        onCancel={() => {
          setDeleteDialog(undefined)
        }}
        onConfirm={handleDelete}
        title={t(`${NS}.partner.delete.title`)}
      />
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </PageLayout>
  )
}
